/**
 * Debug-mode fake data, used to drive the screens flow when the non-prod
 * SAP tenant doesn't have screen-eligible items / options for the test
 * customer the user is logged in as. Gated behind
 * `window.AL_WINDOWS_CONFIG.debug`.
 *
 * Detection key: every stub item has `SONo === STUB_SO`, which Edit.tsx
 * sniffs to short-circuit the API calls.
 */
import type {
  ItemConfig,
  ScreenColour,
  ScreenItem,
  ScreenOption,
  ScreenSubOption,
} from '@alwindows/shared/api/screens'

export const STUB_SO = 'STUB_SO'

function stubConfig(
  itemNo: string,
  values: Record<string, [string, string, string]>,
): ItemConfig[] {
  // values: { NAME: [Characteristic, Value, Desc] }
  return Object.entries(values).map(([Name, [Characteristic, Value, Desc]]) => ({
    Name,
    Characteristic,
    Value,
    Desc,
    ItemNo: itemNo,
    SONo: STUB_SO,
  }))
}

export const STUB_ITEMS: ScreenItem[] = [
  {
    SONo: STUB_SO,
    ItemNo: '1010',
    ProdType: 'DR',
    ProdDesc: 'Sliding door — 2-panel',
    SalesOrg: '4000',
    Quantity: 1,
    DimReplaceText: '',
    Image: '',
    IsCharactImage: false,
    FixedWindow: false,
    ScreenApply: true,
    Material: 'AL',
    Message: '',
    ItemToItemConfig: {
      results: stubConfig('1010', {
        PROD_TYPE: ['DR', 'Door', 'Product Type'],
        PROD_FAM: ['DS', 'Door Sliding', 'Product Family'],
        FRAME_HT: ['2,110 mm', '2,110 mm', 'Frame Height'],
        FRAME_WID: ['2,410 mm', '2,410 mm', 'Frame Width'],
        BAL_RATING: ['LOW', 'LOW - Very Low Risk', 'Bushfire Attack Level Rating'],
        FM_ALU_COLOUR: ['DU', 'Dune', 'Aluminium Frame Colour'],
        FAMILY: ['FAMILY', 'Living Room', 'Room Location'],
      }),
    },
  },
  {
    SONo: STUB_SO,
    ItemNo: '1020',
    ProdType: 'DR',
    ProdDesc: 'Stacker door — 3-panel',
    SalesOrg: '4000',
    Quantity: 1,
    DimReplaceText: '',
    Image: '',
    IsCharactImage: false,
    FixedWindow: false,
    ScreenApply: true,
    Material: 'AL',
    Message: '',
    ItemToItemConfig: {
      results: stubConfig('1020', {
        PROD_TYPE: ['DR', 'Door', 'Product Type'],
        PROD_FAM: ['DK', 'Door Stacker', 'Product Family'],
        FRAME_HT: ['2,400 mm', '2,400 mm', 'Frame Height'],
        FRAME_WID: ['3,600 mm', '3,600 mm', 'Frame Width'],
        BAL_RATING: ['BAL12_5', 'BAL 12.5', 'Bushfire Attack Level Rating'],
        FM_ALU_COLOUR: ['WH', 'White', 'Aluminium Frame Colour'],
        FAMILY: ['FAMILY', 'Dining Room', 'Room Location'],
      }),
    },
  },
  {
    SONo: STUB_SO,
    ItemNo: '1030',
    ProdType: 'WD',
    ProdDesc: 'Sliding window',
    SalesOrg: '4000',
    Quantity: 2,
    DimReplaceText: '',
    Image: '',
    IsCharactImage: false,
    FixedWindow: false,
    ScreenApply: true,
    Material: 'AL',
    Message: '',
    ItemToItemConfig: {
      results: stubConfig('1030', {
        PROD_TYPE: ['WD', 'Window', 'Product Type'],
        PROD_FAM: ['WS', 'Window Sliding', 'Product Family'],
        FRAME_HT: ['1,200 mm', '1,200 mm', 'Frame Height'],
        FRAME_WID: ['1,800 mm', '1,800 mm', 'Frame Width'],
        BAL_RATING: ['LOW', 'LOW - Very Low Risk', 'Bushfire Attack Level Rating'],
        FM_ALU_COLOUR: ['DU', 'Dune', 'Aluminium Frame Colour'],
        FAMILY: ['FAMILY', 'Bedroom', 'Room Location'],
      }),
    },
  },
  {
    SONo: STUB_SO,
    ItemNo: '1040',
    ProdType: 'WD',
    ProdDesc: 'Fixed window',
    SalesOrg: '4000',
    Quantity: 1,
    DimReplaceText: '',
    Image: '',
    IsCharactImage: false,
    FixedWindow: true,
    ScreenApply: false,
    Material: 'AL',
    Message: 'Screens not available for fixed windows.',
    ItemToItemConfig: {
      results: stubConfig('1040', {
        PROD_TYPE: ['WD', 'Window', 'Product Type'],
        PROD_FAM: ['WF', 'Window Fixed', 'Product Family'],
        FRAME_HT: ['900 mm', '900 mm', 'Frame Height'],
        FRAME_WID: ['1,200 mm', '1,200 mm', 'Frame Width'],
        BAL_RATING: ['LOW', 'LOW - Very Low Risk', 'Bushfire Attack Level Rating'],
        FM_ALU_COLOUR: ['WH', 'White', 'Aluminium Frame Colour'],
        FAMILY: ['FAMILY', 'Bathroom', 'Room Location'],
      }),
    },
  },
]

// ----- Configurator stubs (Edit page) -----

const meshOptions: ScreenSubOption[] = [
  {
    ProdType: 'CHARC',
    Desc: 'Charcoal',
    ProdFam: 'DS',
    Price: '0.00',
    NonTradeCharge: '0.00',
    Image: '',
    Sortorder: 1,
    InfoText: 'Standard fibreglass mesh.',
  },
  {
    ProdType: 'GREY',
    Desc: 'Grey',
    ProdFam: 'DS',
    Price: '0.00',
    NonTradeCharge: '0.00',
    Image: '',
    Sortorder: 2,
  },
  {
    ProdType: 'PETPRO',
    Desc: 'Pet-resistant mesh',
    ProdFam: 'DS',
    Price: '85.00',
    NonTradeCharge: '0.00',
    Image: '',
    Sortorder: 3,
    InfoText: 'Heavier weave for clawed friends.',
  },
]

const grillOptions: ScreenSubOption[] = [
  {
    ProdType: 'NONE',
    Desc: 'No grill',
    ProdFam: 'DS',
    Price: '0.00',
    NonTradeCharge: '0.00',
    Image: '',
    Sortorder: 1,
  },
  {
    ProdType: 'COLBND',
    Desc: 'Colonial bands',
    ProdFam: 'DS',
    Price: '120.00',
    NonTradeCharge: '0.00',
    Image: '',
    Sortorder: 2,
  },
  {
    ProdType: 'DIAMND',
    Desc: 'Diamond grille',
    ProdFam: 'DS',
    Price: '180.00',
    NonTradeCharge: '0.00',
    Image: '',
    Sortorder: 3,
  },
]

const petFlapOptions: ScreenSubOption[] = [
  {
    ProdType: 'PF_NONE',
    Desc: 'No pet flap',
    ProdFam: 'DS',
    Price: '0.00',
    NonTradeCharge: '0.00',
    Image: '',
    Sortorder: 1,
  },
  {
    ProdType: 'PF_SM',
    Desc: 'Small (cat / small dog)',
    ProdFam: 'DS',
    Price: '210.00',
    NonTradeCharge: '0.00',
    Image: '',
    Sortorder: 2,
  },
  {
    ProdType: 'PF_LG',
    Desc: 'Large (medium / large dog)',
    ProdFam: 'DS',
    Price: '290.00',
    NonTradeCharge: '0.00',
    Image: '',
    Sortorder: 3,
  },
]

export const STUB_OPTIONS: ScreenOption[] = [
  {
    ProdType: 'BR',
    Desc: 'Barrier screen',
    ProdFam: 'DS',
    Price: '480.00',
    FitCost: '90.00',
    TripleLockCost: '70.00',
    NonTradeCharge: '0.00',
    Sortorder: 1,
    IsMeshType: true,
    IsGrillType: true,
    IsPetFlap: true,
    Image: '',
    InfoText: 'Heavy-duty steel-mesh barrier screen — supports mesh, grill, pet flap and triple lock.',
    ScreenToMesh: { results: meshOptions },
    ScreenToGrill: { results: grillOptions },
    ScreenToPetFlap: { results: petFlapOptions },
  },
  {
    ProdType: 'RET',
    Desc: 'Retractable insect screen',
    ProdFam: 'DS',
    Price: '320.00',
    FitCost: '60.00',
    TripleLockCost: '0.00',
    NonTradeCharge: '0.00',
    Sortorder: 2,
    IsMeshType: true,
    IsGrillType: false,
    IsPetFlap: false,
    Image: '',
    InfoText: 'Roll-away mesh screen — out of sight when not in use.',
    ScreenToMesh: { results: meshOptions.slice(0, 2) },
    ScreenToGrill: { results: [] },
    ScreenToPetFlap: { results: [] },
  },
  {
    ProdType: 'SS',
    Desc: 'Sliding insect screen',
    ProdFam: 'DS',
    Price: '180.00',
    FitCost: '40.00',
    TripleLockCost: '0.00',
    NonTradeCharge: '0.00',
    Sortorder: 3,
    IsMeshType: true,
    IsGrillType: false,
    IsPetFlap: false,
    Image: '',
    ScreenToMesh: { results: meshOptions.slice(0, 2) },
    ScreenToGrill: { results: [] },
    ScreenToPetFlap: { results: [] },
  },
]

export const STUB_COLOURS: ScreenColour[] = [
  { ColourCode: 'WH', Desc: 'White', Sortorder: 1, SalesOrg: '' },
  { ColourCode: 'DU', Desc: 'Dune', Sortorder: 2, SalesOrg: '' },
  { ColourCode: 'PB', Desc: 'Paperbark', Sortorder: 3, SalesOrg: '' },
  { ColourCode: 'CL', Desc: 'Classic Cream', Sortorder: 4, SalesOrg: '' },
  { ColourCode: 'WG', Desc: 'Woodland Grey', Sortorder: 5, SalesOrg: '' },
  { ColourCode: 'MO', Desc: 'Monument', Sortorder: 6, SalesOrg: '' },
  { ColourCode: 'BL', Desc: 'Black', Sortorder: 7, SalesOrg: '' },
]
